<script>
import baseMixin from '@/mixins/baseMixin'
import dayjs from '@/services/date'
import { TimesheetOperationTypeInts } from '@/constants/disputes/TimesheetOperationType'
import timesheetMixin from '@/mixins/timesheetMixin'
import { highlightText } from '@/helpers/search-helpers'
import { DID_NOT_HAPPEN_DELTA_CODE } from '@/constants/disputes/DisputesDeltaCodes'

export default {
  name: 'DisputesTableBreaks',
  mixins: [baseMixin, timesheetMixin],
  props: {
    dispute: {
      type: Object,
      required: true,
    },
    search: {
      type: String,
      default: '',
    },
  },
  computed: {
    candidateHasSubmitted() {
      return this.dispute.hasCandidateTimesheet
    },
    clientHasSubmitted() {
      return this.dispute.hasClientTimesheet
    },
    hasCandidateModifiedTimesheet() {
      if (!this.candidateHasSubmitted) return false
      return (
        this.dispute.candidateTimesheet.operationType ===
        TimesheetOperationTypeInts.MODIFY
      )
    },
    hasClientModifiedTimesheet() {
      if (!this.clientHasSubmitted) return false
      return (
        this.dispute.clientTimesheet.operationType ===
        TimesheetOperationTypeInts.MODIFY
      )
    },
    isDidNotHappen() {
      return this.dispute.deltaMinutes === DID_NOT_HAPPEN_DELTA_CODE
    },
    getLatestAndPreviousBreakMinutes() {
      const candidateSubmittedDate = this.hasCandidateModifiedTimesheet
        ? this.dispute.candidateTimesheet?.submittedDateUtc
        : null
      const clientSubmittedDate = this.hasClientModifiedTimesheet
        ? this.dispute.clientTimesheet?.submittedDateUtc
        : null

      let latestBreaks = this.dispute.breakMinutes

      let previousBreaks = null

      if (candidateSubmittedDate && clientSubmittedDate) {
        const candidateTimesheetIsLatest = dayjs(
          candidateSubmittedDate
        ).isAfter(dayjs(clientSubmittedDate))

        latestBreaks = candidateTimesheetIsLatest
          ? this.dispute.candidateTimesheet.breakMinutes
          : this.dispute.clientTimesheet.breakMinutes

        previousBreaks = !candidateTimesheetIsLatest
          ? this.dispute.candidateTimesheet.breakMinutes
          : this.dispute.clientTimesheet.breakMinutes
      } else if (candidateSubmittedDate) {
        latestBreaks = this.dispute.candidateTimesheet.breakMinutes
        previousBreaks = this.dispute.breakMinutes
      } else if (clientSubmittedDate) {
        latestBreaks = this.dispute.clientTimesheet.breakMinutes
        previousBreaks = this.dispute.breakMinutes
      }

      return {
        latest: this.compileBreaksString(latestBreaks),
        previous: previousBreaks
          ? this.compileBreaksString(previousBreaks)
          : null,
      }
    },
    showPreviousBreaks() {
      return !!this.getLatestAndPreviousBreakMinutes.previous
    },
    strikeThroughPreviousBreaks() {
      if (!this.showPreviousBreaks) return false
      return (
        this.getLatestAndPreviousBreakMinutes.previous !==
        this.getLatestAndPreviousBreakMinutes.latest
      )
    },
  },
  methods: {
    highlightText,
    compileBreaksString(breakMinutes) {
      return `${breakMinutes} mins`
    },
  },
}
</script>

<template>
  <div>
    <div>
      <span
        v-if="getLatestAndPreviousBreakMinutes.latest.length && !isDidNotHappen"
        v-sanitized-html="
          highlightText(getLatestAndPreviousBreakMinutes.latest, search)
        "
      ></span>
      <span v-else>{{ $t('general.emptyCell') }}</span>
    </div>
    <div
      v-if="showPreviousBreaks && !isDidNotHappen"
      :class="[
        strikeThroughPreviousBreaks ? 'text-decoration-line-through' : '',
        'text-caption opacity-5',
      ]"
    >
      <span
        v-sanitized-html="
          highlightText(getLatestAndPreviousBreakMinutes.previous, search)
        "
      ></span>
    </div>
  </div>
</template>
